/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _6 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>6 - Асинхронные функции</h2>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>6.1 - Введение</h3>
      <p className={styles.description}>
        Работа с бекендом может быть довольно сложной, после одной асинхронной операции необходимо сделать еще один
        запрос на сервер по полученным данным и так несколько раз.{' '}
      </p>
      <p className={styles.description}>Представьте следующую ситуацию: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Нам необходимо запросить профиль пользователя от <code className={styles.code}>/user-profile</code>
        </li>
        <li className={styles.list__item}>
          После того как мы получили профиль, нам необходимо запросить всех друзей этого пользователя от{' '}
          <code className={styles.code}>/users/:userId/friends</code>
        </li>
        <li className={styles.list__item}>
          Мы можем запрашивать данные только одного из друзей за одно обращение к серверу, допустим{' '}
          <code className={styles.code}>/users/:userId</code>
        </li>
        <li className={styles.list__item}>
          И только после того как мы запросили всех друзей, мы можем отобразить это пользователю.
        </li>
      </ul>
      <p className={styles.description}>
        Реализация такого алгоритма псевдокодом на промисах может выглядеть следующим образом:
      </p>
      <CopyBlock
        text={`fetch('/user-profile')
  .then(user => fetch(\`/users/$ {user.id}/friends\`))
  .then(idList => {
    const friends = idList.map(id => fetch(\`/users/$ {id}\`));
    return Promise.all(friends);
  })
  .then(friends => console.log(friends))
  .catch(error => console.error(error));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Согласитесь, не самый легкочитаемый код, при том, что операции очень простые. Внутри{' '}
        <code className={styles.code}>then</code> мы передаем функции-обработчики и в результате получаются елки
        вложенности. Вот бы можно было сделать так, что наш код выглядел следующим образом.{' '}
      </p>
      <CopyBlock
        text={`const promise = fetch('/users');
/*
 * Как-то заставить JS подождать пока выполнится промис
 * После чего просто использовать данные, без then и т. д.
 */
console.log(promise.result);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Но проблема в том, что JavaScript не многопоточный язык, поэтому любое ожидание в коде блокирует весь скрипт.
        Например когда мы используем <code className={styles.code}>alert</code> и все виснет до момента выхода из него.
        На помощь приходят генераторы.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>6.2 - Асинхронные функции</h3>
      <p className={styles.description}>
        <b>Асинхронные функции (async/await)</b> - прослойка над промисами и генераторами, которая абстрагирует
        сложности их использования и предоставляет удобный интерфейс.{' '}
      </p>
      <p className={styles.description}>
        Для создания асинхронной функции перед <code className={styles.code}>function</code> добавляем ключевое слово{' '}
        <code className={styles.code}>async</code>. После чего, внутри такой функции мы можем использовать оператор{' '}
        <code className={styles.code}>await</code> и справа от <code className={styles.code}>await</code> поставить
        что-то, что вернет промис. Всегда в таком порядке <code className={styles.code}>await -> promise</code>.{' '}
      </p>
      <CopyBlock
        text={`const getUsers = async () => {
  const response = await fetch('https://jsonplaceholder.typicode.com/users');
  const users = response.json();

  return users;
};

getUsers().then(users => console.log(users));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        При такой записи, <code className={styles.code}>await</code> приостановит только эту функцию (не весь скрипт) и
        будет ждать, пока не выполнится промис. Как только промис выполнился - исполнение функции возобновляется и на
        строке ниже нам доступен результат асинхронной операции.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Асинхронная функция всегда возвращает промис. Даже если написать{' '}
          <code className={styles.code}>return 'hello'</code>, строка будет обернута в промис, который резолвится с этой
          строкой.
        </li>
        <li className={styles.list__item}>
          Оператор <code className={styles.code}>await</code> приостанавливает функцию до того момента, когда промис
          выполнился, то есть перешел в состояние <code className={styles.code}>Settled</code>
        </li>
        <li className={styles.list__item}>
          Оператор <code className={styles.code}>await</code> или вернет значение успешного (
          <code className={styles.code}>Fullfilled</code>) промиса, или выбросит ошибку в случае если промис был
          отклонен (<code className={styles.code}>Rejected</code>)
        </li>
        <li className={styles.list__item}>
          Оператор <code className={styles.code}>await</code> можно использовать только в теле асинхронной функции.
        </li>
        <li className={styles.list__item}>
          Можно использовать любые методы промисов внутри асинхронной функции, к примеру{' '}
          <code className={styles.code}>Promise.all</code>
        </li>
      </ul>
      <p className={styles.description}>
        Для того чтобы обработать ошибку используется конструкция <code className={styles.code}>try/catch</code> -
        аналог блока <code className={styles.code}>catch</code>.{' '}
      </p>
      <CopyBlock
        text={`const getUsers = async () => {
  try {
    const result = await fetch('https://jsonplaceholder.typicode.com/users');
    console.log(result);
  } catch (err) {
    throw err;
  }
};

getUsers()
  .then(users => console.log(users))
  .catch(error => console.log(error));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Не забывайте ставить <code className={styles.code}>await</code> перед промисом
        </li>
        <li className={styles.list__item}>
          Не используйте <code className={styles.code}>await</code> в методах вроде{' '}
          <code className={styles.code}>map</code>, <code className={styles.code}>filter</code> и т. д., работает не так
          как можно ожидать
        </li>
        <li className={styles.list__item}>
          Хотя код выглядит синхронным, не забывайте, что исполнение кода в такой функции приостанавливается и
          возобновляется с течением времени.
        </li>
      </ul>
      <p className={styles.description}>Теперь перепишем наш код, используя асинхронную функцию. </p>
      <CopyBlock
        text={`const getUserFriends = async () => {
  const user = await fetch('/user-profile');
  const idList = await fetch(\`/users/$ {user.id}/friends\`);
  const promises = idList.map(id => fetch(\`/users/$ {id}\`));
  const friends = await Promise.all(promises);

  return friends;
};

// Асинхронная функция всегда вернет промис
const promise = getUserFriends();
promise.then(friends => console.log(friends));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
